import { api } from "../api";
import {
  IntegrationResponse,
  GoogleAdsLoginResponse,
  GoogleAdsCustomersApiResponse,
  GoogleAdsConnectedCustomersResponse,
  GoogleAdsCustomer,
  MetaAdsAccountsResponse,
  MetaAdsAccount,
  MetaAdsLoginResponse,
} from "../../types/integrations";
import { mockGuard } from "../mocks/mockGuard";
import {
  mockIntegrationStatus,
  mockGoogleAdsConnectedCustomers,
  mockGoogleAdsCustomers,
  mockMetaAdsConnectedAccounts,
  mockMetaAdsAccounts,
  mockVoid,
} from "../mocks/mockIntegrations";

let integrationStatusCache: IntegrationResponse | null = null;
let integrationStatusCacheAt = 0;
let pendingIntegrationStatusRequest: Promise<IntegrationResponse> | null = null;
const INTEGRATION_STATUS_CACHE_TTL_MS = 30000;

const googleAdsCustomersCache = new Map<
  number,
  { data: GoogleAdsCustomersApiResponse; at: number }
>();
const googleAdsCustomersPending = new Map<
  number,
  Promise<GoogleAdsCustomersApiResponse>
>();
const GOOGLE_ADS_CUSTOMERS_CACHE_TTL_MS = 30000;

let googleAdsConnectedCustomersCache: GoogleAdsConnectedCustomersResponse | null =
  null;
let googleAdsConnectedCustomersCacheAt = 0;
let pendingGoogleAdsConnectedCustomersRequest: Promise<GoogleAdsConnectedCustomersResponse> | null =
  null;
const GOOGLE_ADS_CONNECTED_CUSTOMERS_CACHE_TTL_MS = 30000;

const metaAdsAccountsCache = new Map<
  number,
  { data: MetaAdsAccountsResponse; at: number }
>();
const metaAdsAccountsPending = new Map<
  number,
  Promise<MetaAdsAccountsResponse>
>();
const META_ADS_ACCOUNTS_CACHE_TTL_MS = 30000;

let metaAdsConnectedAccountsCache: MetaAdsAccountsResponse | null = null;
let metaAdsConnectedAccountsCacheAt = 0;
let pendingMetaAdsConnectedAccountsRequest: Promise<MetaAdsAccountsResponse> | null =
  null;
const META_ADS_CONNECTED_ACCOUNTS_CACHE_TTL_MS = 30000;

export const invalidateIntegrationStatusCache = (): void => {
  integrationStatusCache = null;
  integrationStatusCacheAt = 0;
  pendingIntegrationStatusRequest = null;
};

export const invalidateGoogleAdsCaches = (): void => {
  googleAdsCustomersCache.clear();
  googleAdsCustomersPending.clear();
  googleAdsConnectedCustomersCache = null;
  googleAdsConnectedCustomersCacheAt = 0;
  pendingGoogleAdsConnectedCustomersRequest = null;
};

export const invalidateMetaAdsCaches = (): void => {
  metaAdsAccountsCache.clear();
  metaAdsAccountsPending.clear();
  metaAdsConnectedAccountsCache = null;
  metaAdsConnectedAccountsCacheAt = 0;
  pendingMetaAdsConnectedAccountsRequest = null;
};

// Google Ads Integration
export const getGoogleAdsLoginUrl =
  async (): Promise<GoogleAdsLoginResponse> => {
    try {
      const response = await api.get("/integrations/google-ads/oauth/login");
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error("Endpoint do Google Ads não implementado no backend");
      } else if (error.response?.status === 500) {
        throw new Error("Erro interno do servidor ao conectar com Google Ads");
      } else if (error.response?.status === 401) {
        throw new Error("Não autorizado. Faça login novamente.");
      } else if (error.response?.status === 403) {
        throw new Error("Acesso negado. Verifique suas permissões.");
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error.message) {
        throw new Error(`Erro ao conectar com Google Ads: ${error.message}`);
      } else {
        throw new Error("Erro desconhecido ao conectar com Google Ads");
      }
    }
  };

export const googleAdsCallback = async (
  code: string,
  state: string,
): Promise<void> => {
  try {
    const response = await api.post("/integrations/google-ads/oauth/callback", {
      authCode: code,
      state: state,
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Callback do Google Ads não implementado no backend");
    }
    throw error;
  }
};

const getGoogleAdsCustomersReal = async (
  page: number = 1,
): Promise<GoogleAdsCustomersApiResponse> => {
  const cached = googleAdsCustomersCache.get(page);
  if (cached && Date.now() - cached.at < GOOGLE_ADS_CUSTOMERS_CACHE_TTL_MS) {
    return cached.data;
  }

  const pending = googleAdsCustomersPending.get(page);
  if (pending) {
    return pending;
  }

  const request = (async () => {
    try {
      const response = await api.get("/integrations/google-ads/api/customers", {
        params: { page },
        _suppressErrorMessage: true,
      });
      const data = response.data as GoogleAdsCustomersApiResponse;
      googleAdsCustomersCache.set(page, { data, at: Date.now() });
      return data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error(
          "Lista de clientes do Google Ads não implementada no backend",
        );
      }
      throw error;
    } finally {
      googleAdsCustomersPending.delete(page);
    }
  })();

  googleAdsCustomersPending.set(page, request);
  return request;
};

const getGoogleAdsConnectedCustomersReal =
  async (): Promise<GoogleAdsConnectedCustomersResponse> => {
    if (
      googleAdsConnectedCustomersCache &&
      Date.now() - googleAdsConnectedCustomersCacheAt <
        GOOGLE_ADS_CONNECTED_CUSTOMERS_CACHE_TTL_MS
    ) {
      return googleAdsConnectedCustomersCache;
    }

    if (pendingGoogleAdsConnectedCustomersRequest) {
      return pendingGoogleAdsConnectedCustomersRequest;
    }

    pendingGoogleAdsConnectedCustomersRequest = (async () => {
      try {
        const response = await api.get("/integrations/google-ads/customers", {
          _suppressErrorMessage: true,
        });
        googleAdsConnectedCustomersCache = response.data;
        googleAdsConnectedCustomersCacheAt = Date.now();
        return response.data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          throw new Error(
            "Lista de clientes conectados do Google Ads não implementada no backend",
          );
        }
        throw error;
      } finally {
        pendingGoogleAdsConnectedCustomersRequest = null;
      }
    })();

    return pendingGoogleAdsConnectedCustomersRequest;
  };

const saveGoogleAdsCustomersReal = async (
  customers: { id: string; name: string }[],
): Promise<void> => {
  try {
    const response = await api.post("/integrations/google-ads/customers", {
      customers,
    });
    invalidateGoogleAdsCaches();
    invalidateIntegrationStatusCache();
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error(
        "Salvamento de clientes do Google Ads não implementado no backend",
      );
    }
    throw error;
  }
};

const revokeGoogleAdsReal = async (): Promise<void> => {
  try {
    const response = await api.post("/integrations/google-ads/oauth/revoke");
    invalidateGoogleAdsCaches();
    invalidateIntegrationStatusCache();
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Revogação do Google Ads não implementada no backend");
    }
    throw error;
  }
};

// Meta Ads Integration
export const getMetaAdsLoginUrl = async (): Promise<MetaAdsLoginResponse> => {
  try {
    const response = await api.get("/integrations/meta-ads/oauth/login");
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Endpoint do Meta Ads não implementado no backend");
    } else if (error.response?.status === 500) {
      throw new Error("Erro interno do servidor ao conectar com Meta Ads");
    } else if (error.response?.status === 401) {
      throw new Error("Não autorizado. Faça login novamente.");
    } else if (error.response?.status === 403) {
      throw new Error("Acesso negado. Verifique suas permissões.");
    } else if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    } else if (error.message) {
      throw new Error(`Erro ao conectar com Meta Ads: ${error.message}`);
    } else {
      throw new Error("Erro desconhecido ao conectar com Meta Ads");
    }
  }
};

export const metaAdsCallback = async (
  code: string,
  state: string,
): Promise<void> => {
  try {
    const response = await api.post("/integrations/meta-ads/oauth/callback", {
      authCode: code,
      state: state,
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Callback do Meta Ads não implementado no backend");
    }
    throw error;
  }
};

// O backend retorna a lista de contas conectadas como array puro (igual ao
// endpoint equivalente do Google Ads), mas o endpoint de "todas as contas"
// retorna um objeto { accounts: [...] }. Normalizamos aqui para sempre
// devolver { accounts: [...] }, já que é o formato que o restante do app
// espera — sem isso, contas já conectadas eram descartadas silenciosamente.
const normalizeMetaAdsAccountsResponse = (
  data: unknown,
): MetaAdsAccountsResponse => {
  if (Array.isArray(data)) {
    return { accounts: data as MetaAdsAccount[] };
  }
  if (data && typeof data === "object" && Array.isArray((data as any).accounts)) {
    return data as MetaAdsAccountsResponse;
  }
  return { accounts: [] };
};

const getMetaAdsAccountsReal =
  async (): Promise<MetaAdsAccountsResponse> => {
    const cached = metaAdsAccountsCache.get(1);
    if (cached && Date.now() - cached.at < META_ADS_ACCOUNTS_CACHE_TTL_MS) {
      return cached.data;
    }

    const pending = metaAdsAccountsPending.get(1);
    if (pending) {
      return pending;
    }

    const request = (async () => {
      try {
        const response = await api.get("/integrations/meta-ads/api/accounts", {
          _suppressErrorMessage: true,
        });
        const data = normalizeMetaAdsAccountsResponse(response.data);
        metaAdsAccountsCache.set(1, { data, at: Date.now() });
        return data;
      } catch (error: any) {
        if (error.response?.status === 404) {
          throw new Error(
            "Lista de contas do Meta Ads não implementada no backend",
          );
        }
        throw error;
      } finally {
        metaAdsAccountsPending.delete(1);
      }
    })();

    metaAdsAccountsPending.set(1, request);
    return request;
  };

const getMetaAdsConnectedAccountsReal =
  async (): Promise<MetaAdsAccountsResponse> => {
    if (
      metaAdsConnectedAccountsCache &&
      Date.now() - metaAdsConnectedAccountsCacheAt <
        META_ADS_CONNECTED_ACCOUNTS_CACHE_TTL_MS
    ) {
      return metaAdsConnectedAccountsCache;
    }

    if (pendingMetaAdsConnectedAccountsRequest) {
      return pendingMetaAdsConnectedAccountsRequest;
    }

    pendingMetaAdsConnectedAccountsRequest = (async () => {
      try {
        const response = await api.get("/integrations/meta-ads/accounts", {
          _suppressErrorMessage: true,
        });
        const normalized = normalizeMetaAdsAccountsResponse(response.data);
        metaAdsConnectedAccountsCache = normalized;
        metaAdsConnectedAccountsCacheAt = Date.now();
        return normalized;
      } catch (error: any) {
        if (error.response?.status === 404) {
          throw new Error(
            "Lista de contas conectadas do Meta Ads não implementada no backend",
          );
        }
        throw error;
      } finally {
        pendingMetaAdsConnectedAccountsRequest = null;
      }
    })();

    return pendingMetaAdsConnectedAccountsRequest;
  };

const saveMetaAdsAccountsReal = async (
  accounts: { id: string; name: string }[],
): Promise<void> => {
  try {
    const response = await api.post("/integrations/meta-ads/accounts", {
      accounts,
    });
    invalidateMetaAdsCaches();
    invalidateIntegrationStatusCache();
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error(
        "Salvamento de contas do Meta Ads não implementado no backend",
      );
    }
    throw error;
  }
};

const revokeMetaAdsReal = async (): Promise<void> => {
  try {
    const response = await api.post("/integrations/meta-ads/oauth/revoke");
    invalidateMetaAdsCaches();
    invalidateIntegrationStatusCache();
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("Revogação do Meta Ads não implementada no backend");
    }
    throw error;
  }
};

// General Integration Status
const getIntegrationStatusReal = async (
  forceRefresh = false,
): Promise<IntegrationResponse> => {
  if (!forceRefresh) {
    if (
      integrationStatusCache &&
      Date.now() - integrationStatusCacheAt < INTEGRATION_STATUS_CACHE_TTL_MS
    ) {
      return integrationStatusCache;
    }

    if (pendingIntegrationStatusRequest) {
      return pendingIntegrationStatusRequest;
    }
  }

  pendingIntegrationStatusRequest = (async () => {
    try {
      const response = await api.get("/integrations/status");
      integrationStatusCache = response.data;
      integrationStatusCacheAt = Date.now();
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        const fallback: IntegrationResponse = {
          data: {
            google: {
              isConnected: false,
              customersSelected: false,
            },
            meta: {
              isConnected: false,
              accountsSelected: false,
            },
          },
        };
        integrationStatusCache = fallback;
        integrationStatusCacheAt = Date.now();
        return fallback;
      }
      throw error;
    } finally {
      pendingIntegrationStatusRequest = null;
    }
  })();

  return pendingIntegrationStatusRequest;
};

// Na conta Preview, as funções abaixo retornam dados fictícios (já
// conectados) sem chamar a API real; ações de escrita viram no-op local.
// getGoogleAdsLoginUrl/getMetaAdsLoginUrl não precisam de mock: como o
// status mockado já aparece conectado, os botões de "conectar" nunca
// aparecem na conta Preview.
export const getGoogleAdsCustomers = mockGuard(
  getGoogleAdsCustomersReal,
  mockGoogleAdsCustomers,
);
export const getGoogleAdsConnectedCustomers = mockGuard(
  getGoogleAdsConnectedCustomersReal,
  mockGoogleAdsConnectedCustomers,
);
export const saveGoogleAdsCustomers = mockGuard(
  saveGoogleAdsCustomersReal,
  mockVoid,
);
export const revokeGoogleAds = mockGuard(revokeGoogleAdsReal, mockVoid);
export const getMetaAdsAccounts = mockGuard(
  getMetaAdsAccountsReal,
  mockMetaAdsAccounts,
);
export const getMetaAdsConnectedAccounts = mockGuard(
  getMetaAdsConnectedAccountsReal,
  mockMetaAdsConnectedAccounts,
);
export const saveMetaAdsAccounts = mockGuard(saveMetaAdsAccountsReal, mockVoid);
export const revokeMetaAds = mockGuard(revokeMetaAdsReal, mockVoid);
export const getIntegrationStatus = mockGuard(
  getIntegrationStatusReal,
  mockIntegrationStatus,
);
